import * as React from 'react';
import classnames from 'classnames';

import ActionLink from 'components/buttons/action-link/ActionLink';
import ActionButton from 'components/buttons/action-button/ActionButton';

import { ModalFrame, ModalFrameFooter, ModalFrameMessage, ModalOpener } from '../index';

import { __t, applyTranslations } from 'utils/language';
import { getActionByName } from 'utils/prompt-exit';

import type {
  ActionButtonColor,
  PromptExitAdditionalContent
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));

type Props = {
  open: boolean,
  title?: string | null,
  message?: string | React.ReactNode,
  innerContent?: React.ReactNode,
  confirmButtonColor?: Extract<ActionButtonColor, 'primary' | 'destroy'>,
  confirmButtonLabel?: string,
  cancelButtonLabel?: string,
  proceedButtonLabel?: string,
  additionalContent?: PromptExitAdditionalContent | null,
  modalOpenerClassName?: string,
  onConfirm?: () => void,
  onProceed?: () => void,
  onCancel?: () => void,
  onClose?: () => void
};

class ConfirmModal extends React.PureComponent<Props> {
  render(): React.ReactNode {
    const {
      open,
      onConfirm,
      onProceed,
      onCancel,
      onClose,
      confirmButtonColor,
      confirmButtonLabel,
      cancelButtonLabel,
      proceedButtonLabel,
      innerContent,
      title,
      message,
      additionalContent,
      modalOpenerClassName,
      ...modalProps
    } = this.props;

    const additionalButtons = additionalContent?.buttons?.map(b => ({
      index: b.index,
      component: () => getActionByName(b.actionName) && (
        <ActionButton
          key={b.actionName}
          color={b.color}
          onClick={getActionByName(b.actionName)}
          data={{ qa: b.qaKey }}
        >
          {b.title}
        </ActionButton>
      )
    })) ?? [];
    const buttons = [
      {
        index: 0,
        component: () => onCancel && (
          <ActionButton
            key="cancel-button"
            color="tertiary"
            onClick={onCancel}
            data={{ qa: 'confirm-modal__button-cancel' }}
          >
            {cancelButtonLabel || __t('collabary_common.action_button.cancel')}
          </ActionButton>
        )
      },
      {
        index: 2,
        component: () => onConfirm && (
          <ActionButton
            key="confirm-button"
            color={confirmButtonColor}
            onClick={onConfirm}
            data={{ qa: 'confirm-modal__button-confirm' }}
          >
            {confirmButtonLabel || __t('collabary_common.action_button.confirm')}
          </ActionButton>
        )
      },
      ...additionalButtons
    ].sort((a, b) => a.index - b.index);

    const areButtons = buttons.some(button => button.component());

    const modalOpenerClasses = classnames(modalOpenerClassName);

    return (
      <div className="confirm-modal">
        <ModalOpener
          width="small"
          {...modalProps}
          open={open}
          onClose={onClose || onCancel}
          showClose={Boolean(onClose)}
          containerClass={modalOpenerClasses}
        >
          <ModalFrame
            title={title}
          >
            {
              message &&
                <ModalFrameMessage
                  message={message}
                />
            }
            {innerContent}
            {
              onProceed &&
                <div className="confirm-modal__button-proceed">
                  <ActionLink
                    color="light"
                    size="inherit"
                    onClick={onProceed}
                  >
                    {proceedButtonLabel || __t('collabary_common.action_button.proceed_anyway')}
                  </ActionLink>
                </div>
            }
            {
              areButtons && (
                <ModalFrameFooter>
                  {buttons.map(button => (
                    button.component()
                  ))}
                </ModalFrameFooter>
              )
            }
          </ModalFrame>
        </ModalOpener>
      </div>
    );
  }
}

export default ConfirmModal;
